/**
 * Helpers JSON-LD réutilisables.
 * Tous renvoient des objets sérialisables, à passer au composant <Seo jsonLd={...} />.
 */

const SITE_URL = "https://conceptions.ma";
const SITE_NAME = "Conceptions.ma";
const PHONE = "+212688266025";

export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/og-default.jpg`,
  sameAs: [],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: PHONE,
      contactType: "customer support",
      areaServed: "MA",
      availableLanguage: ["French", "Arabic"],
    },
  ],
  founder: {
    "@type": "Person",
    name: "Adhorizon",
  },
  foundingDate: "2024",
  numberOfEmployees: {
    "@type": "QuantitativeValue",
    minValue: 2,
    maxValue: 10,
  },
  knowsAbout: [
    // Communication publicitaire
    "Agence de communication",
    "Agence de communication Maroc",
    "Agence communication Casablanca",
    "Stratégie de communication",
    "Campagne publicitaire",
    "Publicité digitale",
    "Branding",
    "Identité de marque",
    "Positionnement de marque",
    "Marketing de contenu",
    "Plan média",
    "Communication visuelle",
    "Communication corporate",
    "Communication institutionnelle",
    "Direction artistique",
    "Storytelling de marque",
    "Publicité créative",
    "Agence communication digitale",
    // Conception graphique
    "Design graphique",
    "Design graphique Maroc",
    "Conception graphique",
    "Graphiste freelance",
    "Studio graphique",
    "Logo professionnel",
    "Refonte de logo",
    "Identité visuelle",
    "Charte graphique",
    "Packaging design",
    "Illustration vectorielle",
    "Maquette graphique",
    "Création visuelle",
    "Design print",
    "Design digital",
    "Visuels réseaux sociaux",
    "Bannière publicitaire",
    "Affiche publicitaire",
    "Flyer design",
    "Brochure design",
    "Création d'infographie",
    // Fichiers de fabrication
    "Fichier prêt à imprimer",
    "Fichier de fabrication",
    "Dossier de fabrication",
    "Fichier vectoriel",
    "Fichier source",
    "Fichier haute définition",
    "Fichier résolution 300 dpi",
    "Fichier CMJN",
    "Fichier PDF print",
    "Fichier PDF vectorisé",
    "Fichier AI",
    "Fichier EPS",
    "Fichier SVG",
    "Fichier PSD",
    "Fichier InDesign",
    "Gabarit de fabrication",
    "Fichier pour découpe",
    "Fichier pour sérigraphie",
    "Fichier pour signalétique",
    "Fichier pour packaging",
    // Industripub / CNC
    "Enseigne publicitaire",
    "Enseigne lumineuse",
    "Fichiers CNC",
    "Découpe laser",
    "Signalétique",
    "Habillage façade",
    // Commercial
    "Devis communication",
    "Tarif design graphique",
    "Prix logo professionnel",
    "Agence branding premium",
    "Design graphique professionnel",
    "Impression urgente",
    // Géographique MENA
    "Design graphique Marrakech",
    "Agence communication Algérie",
    "Graphiste Tunis",
    "Agence branding Dubai",
    "Design graphique Riyad",
    "Impression Le Caire",
    "Agence communication Doha",
    "Packaging design Koweït",
    "Communication digitale Libye",
    // Arabe (MENA / darija)
    "وكالة إعلانات",
    "إستراتيجية اتصالات",
    "حملة إعلانية",
    "إعلان رقمي",
    "هوية بصرية",
    "تصميم جرافيك",
    "مصمم جرافيك",
    "تصميم شعار",
    "تصميم تغليف",
    "تصميم مطبوعات",
    "ملفات تصميم",
    "ملف فيكتور",
    "ملف طباعة",
    "ملف ديزاين",
    "ملف جاهز للطباعة",
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: ["fr-MA", "ar-MA"],
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/services?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#business`,
  name: SITE_NAME,
  url: SITE_URL,
  image: `${SITE_URL}/og-default.jpg`,
  telephone: PHONE,
  priceRange: "49 DH – 5000 DH",
  currenciesAccepted: "MAD",
  paymentAccepted: "Virement bancaire, Espèces, Paiement en ligne",
  address: {
    "@type": "PostalAddress",
    addressCountry: "MA",
    addressLocality: "Casablanca",
    addressRegion: "Casablanca-Settat",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 33.5731,
    longitude: -7.5898,
  },
  areaServed: [
    { "@type": "Country", name: "Maroc" },
    { "@type": "City", name: "Casablanca" },
    { "@type": "City", name: "Rabat" },
    { "@type": "City", name: "Tanger" },
    { "@type": "City", name: "Marrakech" },
    { "@type": "City", name: "Agadir" },
    { "@type": "City", name: "Fès" },
    { "@type": "City", name: "Meknès" },
    { "@type": "City", name: "Oujda" },
    { "@type": "City", name: "Kénitra" },
    { "@type": "City", name: "Tétouan" },
    { "@type": "City", name: "Safi" },
    { "@type": "City", name: "El Jadida" },
  ],
  description:
    "Conceptions.ma est l'atelier digital marocain spécialisé dans la création de dossiers complets pour enseignes, fichiers de découpe CNC prêts à fabriquer, identités visuelles et packs métiers. Basé à Casablanca, nous livrons partout au Maroc avec une réponse WhatsApp en moins d'une heure. Dès 49 DH.",
  slogan: "Des fichiers prêts à produire, du premier coup",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Services Conceptions.ma",
    itemListElement: [
      {
        "@type": "OfferCatalog",
        name: "IndustriPub",
        description: "Enseignes, façades, signalétique : dossiers complets CNC-ready",
      },
      {
        "@type": "OfferCatalog",
        name: "DigitalPub",
        description: "Logo, identité visuelle, kits réseaux sociaux",
      },
      {
        "@type": "OfferCatalog",
        name: "PrintPub",
        description: "Cartes, flyers, brochures, packaging prêts pour l'imprimeur",
      },
      {
        "@type": "OfferCatalog",
        name: "3Design",
        description: "Visualisations 3D photo-réalistes pour validation avant fabrication",
      },
      {
        "@type": "OfferCatalog",
        name: "Dev On Demand",
        description: "Sites vitrines et mini-applications métier sur mesure",
      },
    ],
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "09:00",
      closes: "19:00",
    },
  ],
});

export const aggregateRatingSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#business`,
  name: SITE_NAME,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    bestRating: "5",
    ratingCount: "200",
    reviewCount: "200",
  },
});

export const howToSchema = () => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "Comment commander un dossier enseigne CNC-ready au Maroc",
  description:
    "Suivez ces 4 étapes pour recevoir votre dossier complet enseigne avec fichiers de découpe CNC, cotation millimétrique et BAT validé.",
  totalTime: "PT48H",
  estimatedCost: {
    "@type": "MonetaryAmount",
    currency: "MAD",
    value: "49",
  },
  step: [
    {
      "@type": "HowToStep",
      position: 1,
      name: "Décrivez votre projet",
      text: "Envoyez votre brief via WhatsApp ou le Devis Builder en ligne. Photos, croquis, mesures approximatives — tout est utile. Réponse en moins d'une heure.",
      url: `${SITE_URL}/devis`,
    },
    {
      "@type": "HowToStep",
      position: 2,
      name: "Réception des maquettes",
      text: "Notre équipe design + technique prépare les fichiers de découpe, plans cotés et rendus 3D. Livraison en 24 à 48 heures.",
    },
    {
      "@type": "HowToStep",
      position: 3,
      name: "Validation avec votre fabricant",
      text: "Votre artisan ou CNC teste nos fichiers directement. Si quelque chose manque, on le complète gratuitement.",
    },
    {
      "@type": "HowToStep",
      position: 4,
      name: "Réception des fichiers définitifs",
      text: "Pack complet livré sur WhatsApp et par email : fichiers .AI, .DXF, .SVG, cotation millimétrique, fiche matériaux. Prêt à fabriquer.",
    },
  ],
});

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
  })),
});

const DEFAULT_AREA_SERVED = [
  { "@type": "Country", name: "Maroc" },
  { "@type": "City", name: "Casablanca" },
  { "@type": "City", name: "Rabat" },
  { "@type": "City", name: "Marrakech" },
  { "@type": "City", name: "Tanger" },
  { "@type": "City", name: "Agadir" },
  { "@type": "City", name: "Fès" },
  { "@type": "City", name: "Meknès" },
  { "@type": "City", name: "Oujda" },
  { "@type": "City", name: "Kénitra" },
  { "@type": "City", name: "Tétouan" },
];

export const serviceSchema = ({
  name,
  description,
  slug,
  minPrice,
  microservices,
  areaServed,
  serviceType,
}: {
  name: string;
  description: string;
  slug: string;
  minPrice?: number;
  microservices?: { title: string; slug: string; price?: number; description?: string }[];
  areaServed?: Array<{ "@type": string; name: string }>;
  serviceType?: string;
}) => {
  const url = `${SITE_URL}/services/${slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name,
    description,
    ...(serviceType ? { serviceType } : {}),
    provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL, "@id": `${SITE_URL}/#business` },
    areaServed: areaServed && areaServed.length > 0 ? areaServed : DEFAULT_AREA_SERVED,
    availableLanguage: ["fr", "ar"],
    url,
    brand: { "@type": "Brand", name: SITE_NAME },
    ...(minPrice !== undefined && minPrice > 0
      ? {
          offers: {
            "@type": "Offer",
            priceCurrency: "MAD",
            price: minPrice,
            availability: "https://schema.org/InStock",
            url,
          },
        }
      : {}),
    ...(microservices && microservices.length > 0
      ? {
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `${name} — micro-services`,
            itemListElement: microservices.map((m, i) => ({
              "@type": "Offer",
              position: i + 1,
              url: `${SITE_URL}/services/${slug}/${m.slug}`,
              ...(m.price && m.price > 0
                ? { price: m.price, priceCurrency: "MAD", availability: "https://schema.org/InStock" }
                : {}),
              itemOffered: {
                "@type": "Service",
                name: m.title,
                url: `${SITE_URL}/services/${slug}/${m.slug}`,
                ...(m.description ? { description: m.description } : {}),
              },
            })),
          },
        }
      : {}),
  };
};

/** ItemList générique — pour pages catalogue (Services, Microservices). */
export const itemListSchema = ({
  name,
  items,
}: {
  name: string;
  items: { name: string; url: string; description?: string }[];
}) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  numberOfItems: items.length,
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    url: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
    ...(it.description ? { description: it.description } : {}),
  })),
});

export const productSchema = ({
  name,
  description,
  slug,
  price,
  image,
}: {
  name: string;
  description: string;
  slug: string;
  price: number;
  image?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  brand: { "@type": "Brand", name: SITE_NAME },
  ...(image ? { image } : {}),
  offers: {
    "@type": "Offer",
    priceCurrency: "MAD",
    price,
    availability: "https://schema.org/InStock",
    url: `${SITE_URL}/packs/${slug}`,
    seller: { "@type": "Organization", name: SITE_NAME },
  },
});

export const faqPageSchema = (items: { q: string; a: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});
